import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Box, Card, CardContent, CardHeader, TableFooter } from "@mui/material";
import { equals, map } from "ramda";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import makeStyles from "@mui/styles/makeStyles";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import EditIcon from "@mui/icons-material/Edit";
import Typography from "@mui/material/Typography";
import YesNoCard from "../../components/YesNoCard";
import PropTypes from "prop-types";
import {
  useBuyingGroupMemberDelete,
  useBuyingGroupMembers,
} from "../organizationServices";
import {
  AlertContext,
  AlertSeverity,
} from "../../components/alert/AlertProvider";
import BuyingGroupMemberDialog from "../edit/BuyingGroupMemberDialog";
import { Link, useLocation } from "react-router-dom";
import { useSearchFilter } from "../../common/use-searchFilter";
import TablePagination from "@mui/material/TablePagination";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 115,
  },
}));

const BuyingGroupMembersCard = ({ id }) => {
  const classes = useStyles();
  const location = useLocation();
  const { alertError, showMessage } = useContext(AlertContext);

  const { state, handleUpdatePage } = useSearchFilter({
    page: 0,
  });
  const [members, setMembers] = useState([]);
  const membersResponse = useBuyingGroupMembers(id, state.page, 10);
  useEffect(() => {
    if (membersResponse.isSuccess) {
      setMembers(membersResponse.data);
    }
  }, [membersResponse.isSuccess, membersResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleMemberCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleMemberSave = useCallback(
    async data => {
      setDialogOpen(false);
      await membersResponse.refetch();
      showMessage("New Member added to Buying Group.", AlertSeverity.success);
    },
    [showMessage, membersResponse],
  );

  const [waiting, setWaiting] = useState(null);
  const memberDelete = useBuyingGroupMemberDelete();
  const handleDeleteClick = useCallback(
    async memberId => {
      setWaiting(memberId);
      try {
        await memberDelete.mutateAsync({
          resourceIds: { orgId: id, memberId },
        });
        await membersResponse.refetch();
      } catch (err) {
        alertError(err);
      } finally {
        setWaiting(null);
      }
    },
    [id, memberDelete, membersResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Buying Group Members"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Add Member">
              <IconButton onClick={handleAddClick} size="large">
                <AddIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {membersResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography color="error">
              Error loading Buying Group Members.
            </Typography>
          </Box>
        )}
        {membersResponse.isLoading && <PanelLoadingIndicator />}
        {membersResponse.isSuccess && isBlank(members) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Members set up for this Buying Group.
          </Box>
        )}
        {membersResponse.isSuccess && notBlank(members) && (
          <TableContainer>
            <Table stickyHeader aria-label="Members">
              <TableHead>
                <TableRow>
                  <TableCell
                    key="organization"
                    align="left"
                    className={classes.cell}
                  >
                    Organization
                  </TableCell>

                  <TableCell key="alias" align="left" className={classes.cell}>
                    Alias
                  </TableCell>

                  <TableCell
                    key="active"
                    align="center"
                    className={classes.cell}
                  >
                    Active
                  </TableCell>

                  <TableCell
                    key="actions"
                    align="center"
                    style={{ width: 100 }}
                  />
                </TableRow>
              </TableHead>

              <TableBody>
                {map(member => {
                  return (
                    <TableRow hover tabIndex={-1} key={member.organizationId}>
                      <TableCell
                        key={"organization:" + member.organizationId}
                        align="left"
                        style={{ minWidth: 450 }}
                      >
                        <Box>
                          <Typography noWrap variant="body1" component="div">
                            {member.organizationName}
                          </Typography>
                          <Box fontSize="1.2rem">({member.organizationId})</Box>
                        </Box>
                      </TableCell>
                      <TableCell
                        key={"alias:" + member.organizationName}
                        align="left"
                        className={classes.cell}
                      >
                        {member.alias}
                      </TableCell>
                      <TableCell
                        key={"active:" + member.organizationId}
                        align="center"
                        className={classes.cell}
                      >
                        <YesNoCard value={member.active} showNoIcon={false} />
                      </TableCell>
                      <TableCell
                        key={"actions:" + member.organizationId}
                        align="center"
                        style={{ width: 100 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          pl={4}
                        >
                          {notBlank(waiting) &&
                          equals(waiting, member.organizationId) ? (
                            <PanelLoadingIndicator size={25} />
                          ) : (
                            <Fragment>
                              <Tooltip title="Edit">
                                <IconButton
                                  classes={{ label: classes.icon }}
                                  size="large"
                                >
                                  <Link
                                    to={{
                                      pathname: `/manage-organizations/organizations/${member.organizationId}`,
                                    }}
                                    state={{
                                      previousLocation: location.pathname,
                                      label: "Back to Buying Group",
                                    }}
                                  >
                                    <EditIcon
                                      color="primary"
                                      fontSize="large"
                                    />
                                  </Link>
                                </IconButton>
                              </Tooltip>

                              <ConfirmButton
                                handleDelete={() =>
                                  handleDeleteClick(member.organizationId)
                                }
                                dialogTitle="Remove Library Option"
                                dialogBody="Are you sure you want to remove this library option?"
                              />
                            </Fragment>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }, members)}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    classes={{
                      root: classes.pagination,
                    }}
                    rowsPerPageOptions={[10]}
                    colSpan={4}
                    count={-1}
                    rowsPerPage={10}
                    page={state.page}
                    onPageChange={(event, page) => handleUpdatePage(page)}
                    nextIconButtonProps={{
                      disabled: members.length < 10,
                    }}
                    labelDisplayedRows={({ page }) => `Page ${page + 1}`}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      {dialogOpen && (
        <BuyingGroupMemberDialog
          open={dialogOpen}
          organizationId={id}
          onClose={handleMemberCancel}
          onSave={handleMemberSave}
        />
      )}
    </Card>
  );
};

BuyingGroupMembersCard.propTypes = {
  id: PropTypes.string.isRequired,
};

export default BuyingGroupMembersCard;
